import {Component, EventEmitter, Input, OnChanges, Output, SimpleChanges} from '@angular/core';

export interface MemberSearchOption {
    id: string;
    name: string;
    email: string;
}

// Compact "search by name or email" member picker — mirrors the filter/select/hide-with-delay
// pattern already used in create-committee.component.ts and user-card.component.ts, packaged as a
// reusable control so repeated rows (e.g. one per event committee member) don't each need their own
// giant <select> of every chapter member.
@Component({
    selector: 'app-member-search-select',
    templateUrl: './member-search-select.component.html',
    styleUrl: './member-search-select.component.css'
})
export class MemberSearchSelectComponent implements OnChanges {
    @Input() members: MemberSearchOption[] = [];
    @Input() value: string | undefined;
    @Input() placeholder = 'Search member by name or email (optional)';
    @Output() valueChange = new EventEmitter<string | undefined>();

    searchText = '';
    showDropdown = false;
    filteredMembers: MemberSearchOption[] = [];

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['value'] || changes['members']) {
            this.syncSearchTextToValue();
        }
    }

    private syncSearchTextToValue(): void {
        if (!this.value) {
            this.searchText = '';
            return;
        }
        const match = this.members.find(m => m.id === this.value);
        this.searchText = match ? this.displayLabel(match) : this.searchText;
    }

    private displayLabel(member: MemberSearchOption): string {
        return member.email ? `${member.name} (${member.email})` : member.name;
    }

    onInput(): void {
        const input = (this.searchText || '').toLowerCase();
        this.filteredMembers = this.members
            .filter(m => (m.name + ' ' + m.email).toLowerCase().includes(input))
            .slice(0, 20);
        this.showDropdown = !!input && this.filteredMembers.length > 0;
    }

    select(member: MemberSearchOption): void {
        this.searchText = this.displayLabel(member);
        this.value = member.id;
        this.valueChange.emit(member.id);
        this.showDropdown = false;
    }

    clear(): void {
        this.searchText = '';
        this.value = undefined;
        this.valueChange.emit(undefined);
        this.showDropdown = false;
    }

    hideDropdownWithDelay(): void {
        setTimeout(() => this.showDropdown = false, 200);
    }
}
